import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  type CreateVideoPayload,
  type CreateVideoResponse,
  type GetTemplateSchemaResponse,
  createVideo,
  getTemplateSchema,
} from '~/services/VideoGenService';

import TemplateDataForm from './TemplateDataForm';
import TemplateSelect from './TemplateSelect';

type TemplateSchema = GetTemplateSchemaResponse['result'];

interface CreateVideoPageProps {
  open: boolean;
  onClose: () => void;
  onGenerate?: (videoData: { template: string; templateData: Record<string, unknown> }) => void;
  onVideoCreated?: (videoResponse: CreateVideoResponse) => void;
}

export default function CreateVideoPage({ open, onClose, onGenerate, onVideoCreated }: CreateVideoPageProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState<string>('');
  const [template, setTemplate] = useState<string>('');
  const [templateSchema, setTemplateSchema] = useState<TemplateSchema | null>(null);
  const [templateData, setTemplateData] = useState<Record<string, unknown>>({});
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplateSchema = async () => {
      if (template) {
        try {
          const response = await getTemplateSchema(template);
          setTemplateSchema(response.result);
          setTemplateData({});
        } catch (error) {
          enqueueSnackbar('Failed to fetch template schema', { variant: 'error' });
          console.error('Failed to fetch template schema:', error);
          setTemplateSchema(null);
        }
      } else {
        setTemplateSchema(null);
        setTemplateData({});
      }
    };

    fetchTemplateSchema();
  }, [enqueueSnackbar, template]);

  const handleTemplateDataChange = useCallback((data: Record<string, unknown>) => {
    setTemplateData(data);
  }, []);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const payload = {
        name: name,
        templateName: template,
        templateData: templateData,
      } as CreateVideoPayload;
      const response = await createVideo(payload);
      if (onGenerate) {
        onGenerate({
          template,
          templateData,
        });
      }
      if (onVideoCreated) {
        onVideoCreated(response);
      }
      onClose();
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'Failed to create video. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const isFormValid = () => {
    if (!name || name.trim().length < 2) {
      return false;
    }

    if (!template) {
      return false;
    }

    if (templateSchema?.required) {
      return templateSchema.required.every((fieldName: string) => {
        const value = templateData[fieldName];
        if (value === undefined || value === null || value === '') {
          return false;
        }
        if (typeof value === 'string') {
          return value.trim().length > 0;
        }
        return true;
      });
    }

    return true;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5">Create New Video</Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Video Name
            </Typography>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              label="Video Name *"
              fullWidth
              placeholder="Enter a name for your video"
              helperText="Min length: 2 characters, Max length: 100 characters"
              error={name.trim().length > 0 && (name.trim().length < 2 || name.trim().length > 100)}
            />
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Step 1: Choose a template
            </Typography>
            <TemplateSelect value={template} onChange={setTemplate} label="Select Template" />
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Step 2: Fill template data
            </Typography>
            {templateSchema && (
              <Box sx={{ mb: 3 }}>
                <TemplateDataForm
                  schema={templateSchema}
                  onDataChange={handleTemplateDataChange}
                  initialData={templateData}
                />
              </Box>
            )}
            {!template && (
              <Typography variant="body2" color="text.secondary">
                Please select a template first to see the required fields.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit" disabled={isGenerating}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleGenerate} disabled={!isFormValid() || isGenerating}>
          {isGenerating ? 'Generating...' : 'Generate Video'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
